<script setup>
import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { AlertBox, ProjectsFilter, ProjectsList } from '@/app/components';

const { listView } = inject('config');
const store = useStore();
const projects = computed(() => store.state.projectsMap.projectsList);
const lastSearchedValue = computed(() => store.state.projectsMap.lastSearchedValue);
const listError = computed(() => store.state.projectsMap.listError);
</script>

<template>
  <div class="c-projects-list">
    <div class="c-projects-list__head">
      <div class="c-project-list__head-inner">
        <template v-if="lastSearchedValue">
          <h2 class="c-projects-list__title">{{ listView.searchTitle }} {{ lastSearchedValue }}</h2>
        </template>
        <template v-else>
          <h2 class="c-projects-list__title" id="listView">{{ listView.title }}</h2>
          <p v-if="listView.desc" class="c-projects-list__desc">{{ listView.desc }}</p>
          <div class="c-project-list__filter">
            <ProjectsFilter />
          </div>
        </template>
      </div>
    </div>
    <div aria-live="polite" class="c-projects-list__error">
      <AlertBox v-if="listError">
        <p v-html="listError" />
      </AlertBox>
    </div>
    <div v-if="!listError" class="c-project-list__inner" id="projectsList">
      <ProjectsList :projects="projects" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/layouts';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';

.c-projects-list {
  margin-bottom: $spacing-xx-large;
}

.c-projects-list__head {
  background-color: $color-brand-one;
  color: $white;
}

.c-projects-list__title {
  @include h1-style();
  margin: 0;
  color: $white;
}

.c-project-list__head-inner {
  @include center-narrow-content();
  display: flex;
  flex-direction: column;
  gap: $spacing-medium;
  padding: rem(40px) $page-side-padding rem(26px);

  @include mq($from: large) {
    padding: $spacing-x-large 0;
  }
}

.c-projects-list__error:has(div) {
  margin: rem(40px) 0 0;
}

.c-project-list__inner {
  @include center-contain-content();
  display: flex;
  flex-direction: column;
  gap: rem(40px);
  padding: rem(40px) $page-side-padding rem(26px);

  @include mq($from: large) {
    gap: $spacing-x-large;
    padding: $spacing-x-large $page-side-padding;
  }
}

.c-projects-list__desc {
  @include largeprint-style();
  max-width: $limit-x-large;
}

.c-project-list__filter {
  display: flex;
  justify-content: center;
  margin-top: $spacing-medium;
}
</style>
